import { translateLabel } from "@/i18n/messages";
const humanize = (value) => String(value || "")
  .replace(/_/g, " ")
  .toLowerCase()
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const unique = (values) => [...new Set(values.filter(Boolean))];

const formatList = (values) => values.join(" · ");

const named = (item) => item?.park?.name || translateLabel("Unknown destination");

const knownRating = (park) => Number(park?.rating_count || 0) > 0 && Number.isFinite(Number(park?.rating));

const accessFacts = (items) => {
  const facts = [];
  const allAccess = unique(items.flatMap((item) => item.park?.access || []));
  for (const access of allAccess) {
    const parks = items.filter((item) => (item.park?.access || []).includes(access)).map(named);
    if (parks.length === items.length) {
      facts.push({ kind: "access", text: translateLabel("All compared parks list {access} access.", { access: translateLabel(humanize(access)) }) });
    } else if (parks.length) {
      facts.push({ kind: "access", text: translateLabel("{parks} list {access} access.", { parks: formatList(parks), access: translateLabel(humanize(access)) }) });
    }
  }
  return facts.slice(0, 3);
};

const styleFacts = (items) => {
  const allStyles = unique(items.flatMap((item) => item.park?.riding_styles || []));
  const facts = [];
  for (const style of allStyles) {
    const parks = items.filter((item) => (item.park?.riding_styles || []).includes(style)).map(named);
    if (parks.length === 1) {
      facts.push({ kind: "style", text: translateLabel("Only {park} lists {style} riding among these parks.", { park: parks[0], style: translateLabel(humanize(style)) }) });
    }
  }
  return facts.slice(0, 2);
};

export const buildCompareDifferenceSummary = (items = [], {
  routingAvailable = null,
  selectedDate = "",
} = {}) => {
  const validItems = items.filter((item) => item?.park);
  if (validItems.length < 2) return { facts: [], warnings: [] };

  const facts = [];
  const warnings = [];

  const trailValues = validItems.map((item) => ({
    name: named(item),
    count: Number.isFinite(Number(item.park.trail_count)) ? Number(item.park.trail_count) : null,
  }));
  if (trailValues.every((item) => item.count != null) && new Set(trailValues.map((item) => item.count)).size > 1) {
    facts.push({
      kind: "trails",
      text: translateLabel("Catalogue trails: {values}.", { values: trailValues.map((item) => `${item.name} ${item.count}`).join(" · ") }),
    });
    warnings.push(translateLabel("Catalogue trail count is not the number of trails currently open."));
  }

  if (routingAvailable === true) {
    const routes = validItems.map((item) => ({
      name: named(item),
      minutes: item.route?.drive_minutes,
      km: item.route?.drive_km,
    }));
    const known = routes.filter((item) => item.minutes != null);
    if (known.length >= 2) {
      facts.push({
        kind: "travel",
        text: translateLabel("Drive time: {values}.", { values: known.map((item) => `${item.name} ${item.minutes} min${item.km != null ? ` / ${Math.round(item.km)} km` : ""}`).join(" · ") }),
      });
    }
    const missing = routes.filter((item) => item.minutes == null).map((item) => item.name);
    if (missing.length) warnings.push(translateLabel("Drive-time data unavailable for: {parks}.", { parks: formatList(missing) }));
  } else if (routingAvailable === false) {
    warnings.push(translateLabel("Drive-time routing is unavailable, so no travel-time comparison is inferred."));
  }

  facts.push(...accessFacts(validItems));
  facts.push(...styleFacts(validItems));

  const statuses = validItems.map((item) => ({
    name: named(item),
    status: item.park.current_status || "UNKNOWN",
  }));
  if (new Set(statuses.map((item) => item.status)).size > 1) {
    facts.push({
      kind: "status",
      text: translateLabel("Current status: {values}.", { values: statuses.map((item) => `${item.name} ${translateLabel(humanize(item.status))}`).join(" · ") }),
    });
  }

  const ratings = validItems.filter((item) => knownRating(item.park)).map((item) => ({
    name: named(item),
    rating: Number(item.park.rating).toFixed(1),
    count: Number(item.park.rating_count),
  }));
  if (ratings.length >= 2 && new Set(ratings.map((item) => item.rating)).size > 1) {
    facts.push({
      kind: "rating",
      text: translateLabel("Community ratings: {values}.", { values: ratings.map((item) => `${item.name} ${item.rating} (${item.count})`).join(" · ") }),
    });
  }

  if (selectedDate) {
    const weatherKnown = validItems.filter((item) => item.weather).map((item) => ({
      name: named(item),
      condition: humanize(item.weather.condition),
      precip: item.weather.precipitation_probability_pct,
    }));
    if (weatherKnown.length >= 2) {
      facts.push({
        kind: "weather",
        text: translateLabel("{date} forecast: {values}", { date: selectedDate, values: weatherKnown.map((item) => `${item.name} ${translateLabel(item.condition)}${item.precip != null ? ` · ${item.precip}% ${translateLabel("precip.")}` : ""}`).join(" · ") }),
      });
    }
    const missingWeather = validItems.filter((item) => !item.weather).map(named);
    if (missingWeather.length) warnings.push(translateLabel("Forecast unavailable for {date}: {parks}.", { date: selectedDate, parks: formatList(missingWeather) }));
  }

  const verificationMissing = validItems
    .filter((item) => !item.park.visit_info?.last_verified_at)
    .map(named);
  if (verificationMissing.length) {
    warnings.push(translateLabel("Visit-info verification date unavailable for: {parks}.", { parks: formatList(verificationMissing) }));
  }

  return {
    facts: facts.slice(0, 7),
    warnings: unique(warnings),
  };
};
